import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, Loader2, LogOut, FileText, ImageIcon, RefreshCw, Star, Check, Trash2, MessageSquare } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  Pending: "bg-amber-100 text-amber-700",
  Confirmed: "bg-sky-100 text-sky-700",
  Completed: "bg-emerald-100 text-emerald-700",
};

function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="bg-white rounded-2xl border border-sage-200 shadow-sm p-8 w-full max-w-md"
      >
        <div className="w-14 h-14 rounded-full bg-sage-800 flex items-center justify-center mx-auto mb-4">
          <Lock className="text-sage-50" size={24} />
        </div>
        <h1 className="font-heading text-3xl text-sage-900 text-center">Admin Login</h1>
        <p className="text-sage-500 text-sm text-center mb-6">
          Private dashboard — authorized access only.
        </p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full rounded-full bg-sage-800 hover:bg-sage-900 h-11 font-700"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Log In"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Dashboard() {
  const { user, token, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/appointments`, authHeaders);
      setAppts(data);
    } catch (err) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
    loadReviews();
  };

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, authHeaders);
      setReviews(data || []);
    } catch (err) {
      toast.error("Failed to load reviews");
    }
  };

  const approveReview = async (id) => {
    try {
      await axios.patch(`${API}/reviews/${id}/approve`, {}, authHeaders);
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, approved: true } : r)));
      toast.success("Review approved & published");
    } catch (err) {
      toast.error("Failed to approve review");
    }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, authHeaders);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch (err) {
      toast.error("Failed to delete review");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { status }, authHeaders);
      setAppts((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const fileUrl = (path) => `${API}/files/${path}?auth=${token}`;
  const isImage = (name) => /\.(jpg|jpeg|png|webp|gif)$/i.test(name || "");

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl text-sage-900 tracking-tight">Appointments</h1>
          <p className="text-sage-500 text-sm">
            Logged in as {user?.email} · {appts.length} total
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="refresh-btn"
            onClick={load}
            className="rounded-md border-sage-300 text-sage-700"
          >
            <RefreshCw size={16} className="mr-1" /> Refresh
          </Button>
          <Button
            variant="outline"
            data-testid="admin-logout-btn"
            onClick={logout}
            className="rounded-md border-sage-300 text-sage-700"
          >
            <LogOut size={16} className="mr-1" /> Logout
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-sage-400" size={32} />
        </div>
      ) : appts.length === 0 ? (
        <div className="bg-white border border-sage-200 rounded-xl p-16 text-center text-sage-500">
          No appointments yet.
        </div>
      ) : (
        <div className="bg-white border border-sage-200 rounded-xl overflow-x-auto" data-testid="appointments-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-sage-100 text-sage-700 text-left">
                <th className="px-4 py-3 font-700">Patient</th>
                <th className="px-4 py-3 font-700">Contact</th>
                <th className="px-4 py-3 font-700">Service</th>
                <th className="px-4 py-3 font-700">Preferred</th>
                <th className="px-4 py-3 font-700">Notes</th>
                <th className="px-4 py-3 font-700">File</th>
                <th className="px-4 py-3 font-700">Status</th>
                <th className="px-4 py-3 font-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a) => (
                <tr key={a.id} data-testid={`appt-row-${a.id}`} className="border-t border-sage-100 align-top">
                  <td className="px-4 py-3">
                    <div className="font-700 text-sage-900">{a.patient_name}</div>
                    <div className="text-xs text-sage-400">
                      {new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sage-600">
                    <div>{a.phone}</div>
                    {a.email && <div className="text-xs text-sage-400">{a.email}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block bg-sage-100 text-sage-700 rounded-md px-2 py-1 text-xs font-600" data-testid={`appt-service-${a.id}`}>
                      {a.service}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sage-600">
                    {a.preferred_date && <div>{a.preferred_date}</div>}
                    <div className="text-xs text-sage-400">{a.preferred_time}</div>
                  </td>
                  <td className="px-4 py-3 text-sage-600 max-w-[200px]">
                    {a.medical_notes ? (
                      <span className="text-danger text-xs">⚠ {a.medical_notes}</span>
                    ) : a.message ? (
                      <span className="text-xs">{a.message}</span>
                    ) : (
                      <span className="text-sage-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {a.file_path ? (
                      <a
                        href={fileUrl(a.file_path)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`appt-file-${a.id}`}
                        className="inline-flex items-center gap-1 text-sage-600 hover:text-sage-900 underline text-xs"
                      >
                        {isImage(a.file_name) ? <ImageIcon size={14} /> : <FileText size={14} />}
                        View
                      </a>
                    ) : (
                      <span className="text-sage-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`appt-status-${a.id}`}
                      className={`inline-block rounded-full px-2.5 py-1 text-xs font-700 ${STATUS_STYLES[a.status] || STATUS_STYLES.Pending}`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`confirm-btn-${a.id}`}
                        onClick={() => updateStatus(a.id, "Confirmed")}
                        disabled={a.status === "Confirmed"}
                        className="text-xs font-600 px-2.5 py-1 rounded-md bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-40"
                      >
                        Confirm
                      </button>
                      <button
                        data-testid={`complete-btn-${a.id}`}
                        onClick={() => updateStatus(a.id, "Completed")}
                        disabled={a.status === "Completed"}
                        className="text-xs font-600 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                      >
                        Complete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reviews management */}
      <div className="mt-12" data-testid="admin-reviews-panel">
        <div className="flex items-center gap-2 mb-5">
          <MessageSquare className="text-sage-500" size={22} />
          <h2 className="font-heading text-3xl text-sage-900 tracking-tight">Patient Reviews</h2>
          <span className="text-sage-400 text-sm">
            {reviews.filter((r) => !r.approved).length} pending
          </span>
        </div>
        {reviews.length === 0 ? (
          <div className="bg-white border border-sage-200 rounded-xl p-10 text-center text-sage-500">
            No reviews submitted yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="bg-white border border-sage-200 rounded-xl p-5 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-0.5">
                    {[...Array(r.rating || 5)].map((_, s) => (
                      <Star key={s} size={13} className="text-gold fill-current" />
                    ))}
                  </div>
                  <span
                    data-testid={`review-status-${r.id}`}
                    className={`text-[11px] font-700 px-2 py-0.5 rounded-full ${
                      r.approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {r.approved ? "Published" : "Pending"}
                  </span>
                </div>
                <p className="text-sm text-sage-700 mt-3 flex-1">"{r.text}"</p>
                <div className="mt-3 pt-3 border-t border-sage-100">
                  <div className="font-700 text-sage-900 text-sm">{r.name}</div>
                  {r.treatment && <div className="text-xs text-sage-500">{r.treatment}</div>}
                </div>
                <div className="flex gap-2 mt-3">
                  {!r.approved && (
                    <button
                      data-testid={`review-approve-${r.id}`}
                      onClick={() => approveReview(r.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-600 px-2.5 py-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    >
                      <Check size={14} /> Approve
                    </button>
                  )}
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => deleteReview(r.id)}
                    className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-600 px-2.5 py-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();

  return (
    <div className="bg-sage-50 min-h-screen">
      <Header />
      {user === null ? (
        <div className="flex justify-center py-32">
          <Loader2 className="animate-spin text-sage-400" size={32} />
        </div>
      ) : user ? (
        <Dashboard />
      ) : (
        <LoginScreen />
      )}
      <div className="text-center py-6">
        <Link to="/" className="text-sage-400 text-sm hover:text-sage-600">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
